import AppCard from '@nxsys/components/AppCard';
import AppIconCard from '@nxsys/components/AppIconCard';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import { useGetStatisticsInvoicing } from '@nxsys/hooks/api/dashboard';
import { useMarkAllAsPaidInvoice } from '@nxsys/hooks/api/invoice/useInvoiceApi';
import { Icons } from '@nxsys/types/icons';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { showMessage } from '@nxsys/utils/message';
import { Checkbox } from 'antd';
import InvoicingModal, {
  InvoicingModalTypeEnum,
} from './components/InvoicingModal';
import clsx from 'clsx';

type Props = {
  totalPendingInvoiceGeneration?: number;
  isFetchingPendingInvoiceGeneration?: boolean;
  onClickPendingInvoiceGeneration?: () => void;
};

const Invoicing = ({
  totalPendingInvoiceGeneration,
  isFetchingPendingInvoiceGeneration,
  onClickPendingInvoiceGeneration,
}: Props) => {
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const { value, loading, run } = useGetStatisticsInvoicing();
  const data = useMemo(() => value?.data, [value]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState<InvoicingModalTypeEnum | null>(
    null,
  );

  const {
    run: markAllAsPaidInvoiceRun,
    loading: isMarkAllAsPaidInvoicePending,
  } = useMarkAllAsPaidInvoice();

  useEffect(() => {
    run();
  }, []);

  useEffect(() => {
    const handleReload = () => run();
    window.addEventListener('dashboard:reload-invoicing', handleReload);
    return () =>
      window.removeEventListener('dashboard:reload-invoicing', handleReload);
  }, []);

  const handleOpenModal = (type: InvoicingModalTypeEnum) => {
    setModalType(type);
    setIsOpenModal(true);
  };

  const handleMarkAllAsPaid = () => {
    if (!data?.invoiceOutstanding) {
      showMessage({
        type: 'warning',
        content: 'No outstanding invoices to mark as paid',
      });
      return;
    }
    AppConfirmation.open({
      title: 'Mark All as Paid',
      paragraph:
        'Are you sure you want to mark all outstanding invoices as paid?',
      type: 'warning',
      icon: 'IconWarning',
      okText: 'Confirm',
      onConfirm: async () => {
        const res = await markAllAsPaidInvoiceRun({
          datePaid: new Date() as any,
          receipt: 0,
        });
        if (res.success) {
          showMessage({
            type: 'success',
            content: 'Mark all as paid successfully',
          });
          run();
        } else {
          showMessage({
            type: 'error',
            content: 'Mark all as paid failed',
          });
        }
      },
    });
  };

  const items = [
    // Invoice generation is hidden for Ireland *bureau* only: a bureau tenant
    // bills nobody. An Ireland umbrella tenant invoices its agencies, so the
    // tile belongs there — matching menuFilter, where the sidebar entry for the
    // same feature is likewise gated on isBureau && isIreland.
    ...(!(isBureau && isIreland)
      ? [
          {
            title: totalPendingInvoiceGeneration || 0,
            description: 'Pending INV Generation',
            icon: <Icons.IconFileListMinus />,
            onClick: onClickPendingInvoiceGeneration,
            colorClassName: 'bg-warning-100',
            showMarkAllAsPaid: false,
          },
        ]
      : []),
    {
      title: data?.invoiceOutstanding,
      description: 'Outstanding Invoices',
      icon: <Icons.IconFileListBan />,
      onClick: () =>
        handleOpenModal(InvoicingModalTypeEnum.OUTSTANDING_INVOICES),
      colorClassName: 'bg-cyan-100',
      showMarkAllAsPaid: true,
    },
    // {
    //   title: data?.invoicePaid,
    //   description: 'Paid Invoices',
    //   icon: <Icons.IconFileListCheck />,
    //   onClick: () => handleOpenModal(InvoicingModalTypeEnum.PAID_INVOICES),
    //   colorClassName: 'bg-cyan-100',
    // },
    // {
    //   title: data?.invoiceCredit,
    //   description: 'Credit Invoices',
    //   icon: <Icons.IconFileListCheck />,
    //   onClick: () => handleOpenModal(InvoicingModalTypeEnum.CREDIT_INVOICES),
    //   colorClassName: 'bg-cyan-100',
    // },
  ];

  return (
    <div className="Invoicing h-full">
      <AppCard
        className="p-[24px] h-full"
        loading={loading || isFetchingPendingInvoiceGeneration}
      >
        <div className="flex justify-between items-center mb-[24px]">
          <AppIconCard iconName="IconFileText" title="Invoicing" />
        </div>
        <div className="grid grid-cols-1 gap-[16px] max-sm:grid-cols-1">
          {items.map((item) => (
            <div
              key={item.title}
              className="border border-[#CDDDD9] dashboard-hover-card bg-[#F9FBFA] rounded-[8px] p-[16px]"
              onClick={item.onClick}
            >
              <div className="flex items-center justify-between gap-x-[8px] mb-[34px]">
                <div
                  className={clsx('rounded-[8px] p-[8px]', item.colorClassName)}
                >
                  {item.icon}
                </div>
                <Icons.IconArrowRight />
              </div>
              <div className="text-[22px] font-bold mb-[8px]">{item.title}</div>
              <div className="text-[14px] font-bold">{item.description}</div>
              {item.showMarkAllAsPaid && (
                <div
                  className="mt-[12px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={false}
                    disabled={isMarkAllAsPaidInvoicePending}
                    onChange={handleMarkAllAsPaid}
                  >
                    Mark all as paid
                  </Checkbox>
                </div>
              )}
            </div>
          ))}
        </div>
      </AppCard>
      {modalType && (
        <InvoicingModal
          isOpen={isOpenModal}
          toggle={() => setIsOpenModal(false)}
          type={modalType}
          cbAfterAction={run}
        />
      )}
    </div>
  );
};

export default Invoicing;
